import { FC } from "react";
import { useHover } from "react-aria";
import {
  OakBox,
  OakFlex,
  OakHeading,
  OakP,
  OakPrimaryButton,
  OakSecondaryLink,
} from "@oaknational/oak-components";

import ScreenReaderOnly from "../ScreenReaderOnly";

import LineClamp from "@/components/SharedComponents/LineClamp";
import formatDate from "@/utils/formatDate";
import { PostListItemProps } from "@/components/SharedComponents/PostListItem";
import useClickableCard from "@/hooks/useClickableCard";
import { resolveOakHref } from "@/common-lib/urls";

type PostListUpcomingWebinarListItemProps = PostListItemProps & {
  signUpHref: string;
  signUpOnClick: () => void;
};

/**
 * Preview component for an upcoming webinar. Includes a link to the holding
 * page, and a link to the webinar participation 'sign up' form.
 *
 * ## Usage
 *
 * On the webinar listing page, it sits above the list of past webinars, on the
 * first page.
 *
 * ## Caveats
 *
 * If an invalid date is provided, text "Invalid Date" is displayed.
 */
const PostListUpcomingWebinarListItem: FC<
  PostListUpcomingWebinarListItemProps
> = (props) => {
  const { titleTag, title, date, summary, slug, signUpHref, signUpOnClick } =
    props;
  const { containerProps, primaryTargetProps } =
    useClickableCard<HTMLAnchorElement>();
  const { hoverProps: buttonHoverProps } = useHover({});
  return (
    <OakFlex
      {...containerProps}
      $position={"relative"}
      $flexDirection={["column", "row"]}
      $alignItems={["flex-start", "center"]}
      $width={"100%"}
      $font={["body-4", "body-3"]}
    >
      <OakBox $mr="auto">
        <OakP>Coming soon, {formatDate(date, { month: "short" })}</OakP>
        <OakHeading
          tag={titleTag}
          $font={["heading-6", "heading-5"]}
          $mt={"spacing-8"}
        >
          <OakSecondaryLink
            {...primaryTargetProps}
            href={resolveOakHref({ page: "webinar-single", webinarSlug: slug })}
          >
            {title}
          </OakSecondaryLink>
        </OakHeading>
        <OakP $mt="spacing-8">
          <LineClamp lines={2}>{summary}</LineClamp>
        </OakP>
      </OakBox>
      <OakBox
        $mt={["spacing-32", "spacing-0"]}
        $ml={["spacing-0", "spacing-48"]}
      >
        <OakPrimaryButton
          {...buttonHoverProps}
          onClick={signUpOnClick}
          element="a"
          target="_blank"
          href={signUpHref}
          iconName="chevron-right"
          isTrailingIcon={true}
        >
          Save my place
          <ScreenReaderOnly>{`on the webinar: ${title}`}</ScreenReaderOnly>
        </OakPrimaryButton>
      </OakBox>
    </OakFlex>
  );
};

export default PostListUpcomingWebinarListItem;
